export const reRegFrequencyOptions = [
    { value: 0, label: "Semester Based" },
    { value: 1, label: "Annual" },
];

/**
 * Maps a `{value, label}`-shaped list to itself, dropping any extra fields.
 * @param {Array<{value: any, label: string}>} [items] Source list to normalize.
 * @returns {Array<{value: any, label: string}>}
 */
export function mapOptions(items = []) {
    return (items || []).map((item) => ({
        value: item.value,
        label: item.label,
    }));
}

/**
 * Checks whether a flag value (which may come back as a boolean, a number, or a string) is truthy.
 * @param {*} value Flag value to check.
 * @returns {boolean}
 */
function isTruthyFlag(value) {
    return value === true || value === 1 || value === "1";
}

/**
 * Builds the `error`/`errorText` props for a Formik-bound input from its field name,
 * so call sites don't repeat the same `errors`/`touched` check per field.
 * @param {object} formik Formik instance for the form.
 * @param {string} fieldName Name of the field on `formik.values`/`errors`/`touched`.
 * @returns {{error: boolean, errorText: string}}
 */
export function getFieldErrorProps(formik, fieldName) {
    const hasError = !!formik.errors[fieldName];
    const isTouched = !!formik.touched[fieldName];
    return {
        error: hasError && isTouched,
        errorText: hasError && isTouched ? formik.errors[fieldName] : "",
    };
}

/**
 * Finds the version matching the programme's active version_id, falling back to any version flagged active.
 * @param {Array<object>} [versionList] All versions for this programme.
 * @param {string|number} [activeVersionId] The programme's `version_id` to match first.
 * @returns {object|null}
 */
function getActiveVersion(versionList = [], activeVersionId) {
    /*
     * A PMC version is active because it's the one the PMC's own version_id
     * points at — that match takes priority over the flag search below, which
     * PMC versions don't actually carry (see the comment at the call site).
     */
    const matchedByProgrammeVersionId =
        activeVersionId !== undefined && activeVersionId !== null
            ? versionList.find(
                (version) => String(version?.id ?? version?.value) === String(activeVersionId)
            )
            : null;

    if (matchedByProgrammeVersionId) {
        return matchedByProgrammeVersionId;
    }

    return (
        versionList.find((version) =>
            isTruthyFlag(version?.active) ||
            isTruthyFlag(version?.isActive) ||
            isTruthyFlag(version?.is_active) ||
            String(version?.status || "").toLowerCase() === "active"
        ) || null
    );
}

/**
 * Resolves the PMC "Version Summary Strip" state (active version, display name,
 * Active/Locked status, coordinator) from the programme record and version list.
 * @param {object} programmeData The PMC/programme record.
 * @param {Array<object>} [versionList] All versions for this programme.
 * @param {string|number} [selectedVersionId] Resolve this version's own state instead of the active one.
 * @returns {{activeVersion, versionName, isActive, isLocked, coordinator}}
 */
export function resolveVersionSummaryState(programmeData = {}, versionList = [], selectedVersionId) {
    const selectedVersion =
        selectedVersionId !== undefined && selectedVersionId !== null && selectedVersionId !== ""
            ? versionList.find((version) => String(version?.id ?? version?.value) === String(selectedVersionId))
            : null;

    const activeVersion = selectedVersion || getActiveVersion(versionList, programmeData?.version_id);

    const versionName =
        activeVersion?.name ||
        programmeData?.version_name ||
        programmeData?.versionName ||
        "No Active Version";

    const explicitActiveFlag =
        activeVersion?.active ??
        activeVersion?.isActive ??
        activeVersion?.is_active;

    /*
     * PMC versions carry no generic "active" flag of their own — a version is
     * active because it is the one the PMC's own version_id points at.
     */
    const matchesProgrammeActiveVersion =
        activeVersion?.id !== undefined &&
        activeVersion?.id !== null &&
        programmeData?.version_id !== undefined &&
        programmeData?.version_id !== null &&
        String(activeVersion.id) === String(programmeData.version_id);

    const statusSource = activeVersion?.status ?? programmeData?.status;

    const isActive =
        explicitActiveFlag !== undefined
            ? isTruthyFlag(explicitActiveFlag)
            : matchesProgrammeActiveVersion || String(statusSource || "").toLowerCase() === "active";

    const lockSource =
        activeVersion?.isLocked ??
        activeVersion?.is_locked ??
        activeVersion?.locked ??
        activeVersion?.lock_version_settings ??
        programmeData?.isLocked ??
        programmeData?.is_locked ??
        false;

    const isLocked = isTruthyFlag(lockSource);

    return {
        activeVersion,
        versionName,
        isActive,
        isLocked,
        coordinator: activeVersion?.coordinator || programmeData?.coordinator || null,
    };
}
